//TODO split schema and service into separate files, merge with personRepositoryService
/* eslint-disable */
import { useQueries } from "@tanstack/react-query";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { useToasts } from "../hooks/useToasts";
import { statusApiSchema } from "../schemas/status";
import { paginatedResponse } from "../utils";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";

// Schema for creating a new customer person
export const CustomerPersonFormSchema = z
  .object({
    personName: z.string().min(1, "personNameRequired"),
    personSecondName: z.string().optional(),
    personSurname: z.string().min(1, "personSurnameRequired"),
    personEmail: z
      .union([
        z.string().email("invalidEmail"),
        z.string().max(0), // Allow empty string
      ])
      .optional(),
    personPhone: z.string().optional(),
    personPesel: z.string().optional(),
    personIdCard: z.string().optional(),
    personPassport: z.string().optional(),
    addressLivingStreet: z.string().optional(),
    addressLivingBuilding: z.string().optional(),
    addressLivingApartment: z.string().optional(),
    addressLivingCity: z.string().optional(),
    addressLivingZipcode: z.string().optional(),
    addressMailingStreet: z.string().optional(),
    addressMailingBuilding: z.string().optional(),
    addressMailingApartment: z.string().optional(),
    addressMailingCity: z.string().optional(),
    addressMailingZipcode: z.string().optional(),
    extraNote: z.string().min(1, "extraNoteRequired"),
  })
  .refine(
    (data) => {
      const addressLivingFields = [
        data.addressLivingStreet,
        data.addressLivingBuilding,
        data.addressLivingCity,
        data.addressLivingZipcode,
      ];
      const isAddressLivingFilled = addressLivingFields.some(
        (field) => field !== undefined && field !== "",
      );
      const isAddressLivingComplete = addressLivingFields.every(
        (field) => field !== undefined && field !== "",
      );
      return !isAddressLivingFilled || isAddressLivingComplete;
    },
    {
      message: "addresLivingNotFilled",
      path: [
        "addressLivingStreet",
        "addressLivingBuilding",
        "addressLivingApartment",
        "addressLivingCity",
        "addressLivingZipcode",
      ],
    },
  )
  .refine(
    (data) => {
      const addressMailingFields = [
        data.addressMailingStreet,
        data.addressMailingBuilding,
        data.addressMailingCity,
        data.addressMailingZipcode,
      ];
      const isAddressMailingFilled = addressMailingFields.some(
        (field) => field !== undefined && field !== "",
      );
      const isAddressMailingComplete = addressMailingFields.every(
        (field) => field !== undefined && field !== "",
      );
      return !isAddressMailingFilled || isAddressMailingComplete;
    },
    {
      message: "addresMailingNotFilled",
      path: [
        "addressMailingStreet",
        "addressMailingBuilding",
        "addressMailingApartment",
        "addressMailingCity",
        "addressMailingZipcode",
      ],
    },
  );

const PhoneSchema = z.object({
  phone_number: z.string().nullable(),
  phone_note: z.string().nullable(),
});

const EmailSchema = z.object({
  email_address: z.string().nullable(),
});

const AddressSchema = z.object({
  city: z.string(),
  zip_code: z.string(),
  street: z.string(),
  building: z.string(),
  flat_nr: z.string(),
});

const RelatedPersonSchema = z.object({
  person_id: z.string(),
  customer_id: z.string().nullable(),
  name: z.string(),
  surname: z.string(),
  note: z.string(),
});

const ClauseDecisionSchema = z.object({
  id: z.string().nullable(),
  clause_name_eng: z.string(),
  persclau_decision: z.boolean().nullable(),
  created: z.string().nullable(),
});

const RelatedCompanySchema = z.object({
  person_id: z.string(),
  company_id: z.string(),
  name: z.string(),
  company_name: z.string(),
  role: z.string(),
  creator: z.string().nullable(),
});

const IdentificationDocumentSchema = z.object({
  id: z.string(),
  document_type: z.enum([
    "id_card",
    "passport",
    "drivers_license",
    "residence_permit",
    "m_card",
    "other",
  ]),
  expires_at: z.string().nullable(),
  number: z.string(),
  issuing_authority: z.string().nullable(),
});

const IdentificationDocumentCreateSchema = z.object({
  document_type: z.enum([
    "id_card",
    "passport",
    "drivers_license",
    "residence_permit",
    "m_card",
    "other",
  ]),
  expires_at: z.string().nullable().optional(),
  number: z.string().min(1, "Document number is required"),
  issuing_authority: z.string().nullable().optional(),
});

const PersonSchema = z.object({
  id: z.string(),
  ocid: z.string(),
  name: z.string(),
  name2: z.string().nullable(),
  surname: z.string(),
  pesel: z.string().nullable(),
  phone: PhoneSchema,
  email: EmailSchema,
  jdg_nip: z.string().nullable(),
  jdg_regon: z.string().nullable(),
  jdg_name: z.string().nullable(),
  living_address: AddressSchema.nullable(),
  mailing_address: AddressSchema.nullable(),
  related_persons: z.array(RelatedPersonSchema),
  // clause_decisions: z.array(ClauseDecisionSchema),
  related_companies: z.array(RelatedCompanySchema),
  name_of_father: z.string().nullable(),
  name_of_mother: z.string().nullable(),
  financial_status: z.string().nullable(),
  marital_status: z.string().nullable(),
  maiden_surname: z.string().nullable(),
  person_citizenship: z.string().nullable(),
  identification_documents: z.array(IdentificationDocumentSchema),
  has_basic_info: z.boolean(),
  has_detailed_info: z.boolean(),
  has_id_documentation: z.boolean(),
});

const CompanyAddressSchema = AddressSchema.nullable();

const CompanyRelatedPersonSchema = z.object({
  person_id: z.string(),
  customer_id: z.string().nullable(),
  company_id: z.string(),
  company_name: z.string(),
  name: z.string(),
  surname: z.string(),
  role: z.string(),
  creator: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});

const CompanySchema = z.object({
  name: z.string(),
  name_short: z.string(),
  nip: z.string().nullable(),
  regon: z.string().nullable(),
  legal: z.string(),
  krs: z.string().nullable(),
  court: z.string(),
  signature: z.string(),
  capital: z.string(),
  representation: z.string(),
  tax: z.string(),
  email: EmailSchema,
  phone: PhoneSchema,
  address: CompanyAddressSchema,
  related_persons: z.array(CompanyRelatedPersonSchema),
  has_basic_info: z.boolean(),
  company_citizenship: z.string().nullable().optional(),
});

const ReasonSchema = z.object({
  reason_name: z.string(),
  reason_name_eng: z.string(),
});

export const CustomerResponseEntrySchema = z.object({
  id: z.string(),
  note: z.string(),
  status: z.string(),
  person: PersonSchema.nullable(),
  company: CompanySchema.nullable(),
  customer_number: z.number(),
  segment: z.string(),
  reason: ReasonSchema.nullable(),
  seller: z.any().nullable(),
  selected: z.boolean().optional(),
  birth_date: z.string().nullable(),
});

export const RelatedPersonRequestSchema = z
  .object({
    note: z.string().optional(),
  })
  .and(
    z.union([
      z.object({
        person_id: z.string(),
      }),
      z.object({
        person: z.object({
          person_name: z.string(),
          person_surname: z.string(),
          email: z.object({
            email_address: z.string().email(),
          }),
          phone: z.object({
            phone_number: z.string(),
          }),
        }),
      }),
    ]),
  );

const ReasonResponseSchema = z.object({
  id: z.string(),
  reason_name: z.string(),
  reason_name_eng: z.string(),
});

const shortCustomerSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  last_name: z.string().nullable(),
  company_name: z.string().nullable(),
});

export const customersApiSchema = {
  customer: CustomerResponseEntrySchema,
  shortCustomer: shortCustomerSchema,
  getReasonsResonse: paginatedResponse(ReasonResponseSchema),
  person: PersonSchema,
  identificationDocument: IdentificationDocumentSchema,
  identificationDocumentCreate: IdentificationDocumentCreateSchema,
};


export function useGetReasons(options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["customerReasons"],
    queryFn: () => {
      return customersApi.getReasons(axiosPriv);
    },
  });
}

export function useGetPersonSelectList(params = new URLSearchParams(), options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["person-select-list"],
    queryFn: () => {
      return customersApi.getPersonSelectList(axiosPriv, params);
    },
  });
}



export function useCustomers(params = new URLSearchParams(), options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["customers", Object.fromEntries(params.entries())],
    queryFn: () => {
      return customersApi.getCustomers(axiosPriv, params);
    },
  });
}

export function useCustomersShortData(params = new URLSearchParams(), options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["customers-short-data", Object.fromEntries(params.entries())],
    queryFn: () => {
      return customersApi.getCustomersShortData(axiosPriv, params);
    },
  });
}

export function useCustomersApprovedClauses(params = new URLSearchParams(), options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["customers-approved-clauses", Object.fromEntries(params.entries())],
    queryFn: () => {
      return customersApi.getCustomersApprovedClauses(axiosPriv, params);
    },
  });
}

export function useCustomersSymfoniaCode(params = new URLSearchParams(), options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["customers-symfonia-code", Object.fromEntries(params.entries())],
    queryFn: () => {
      return customersApi.getCustomersSymfoniaCode(axiosPriv, params);
    },
  });
}

export function useEditCustomerSymfoniaCodeMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { error: toastErr } = useToasts();

  return useMutation({
    ...options,
    mutationFn: ({id, data}) => {
      return customersApi.updateCustomerSymfoniaCode(
        axiosPriv,
        id,
        data,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["customers-symfonia-code"] });
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      toastErr(error.message);
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
}

export function useCustomersFullData(params = new URLSearchParams(), options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["customers", Object.fromEntries(params.entries())],
    queryFn: () => {
      return customersApi.getCustomersFullData(axiosPriv, params);
    },
  });
}

export function useCustomerById(customerId, options = {}) {
  const axiosPriv = useAxiosPrivate();

  const queryOpts = queryOptions({
    ...options,
    queryKey: ["customers", customerId],
    queryFn: () => {
      return customersApi.getCustomerById(axiosPriv, customerId);
    },
  });

  return useQuery({
    ...queryOpts,
  });
}

export function useCustomerByPersonId(personId, options = {}) {
  const axiosPriv = useAxiosPrivate();

  return useQuery({
    ...options,
    queryKey: ["customers", "byPersonId", personId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("person_id", personId);

      const response = await customersApi.getCustomers(axiosPriv, params);
      return response.results.length > 0 ? response.results[0] : null;
    },
    enabled: !!personId && options.enabled !== false,
  });
}

export function useCustomersByIds(customerIds, options = {}) {
  const axiosPriv = useAxiosPrivate();

  return useQuery({
    ...options,
    queryKey: ["customers", "multiple", customerIds.sort()],
    queryFn: async () => {
      const promises = customerIds.map((id) =>
        customersApi.getCustomerById(axiosPriv, id),
      );
      return Promise.all(promises);
    },
    enabled: customerIds.length > 0,
  });
}

// TODO remove hook above and replace with this:
export function properUseCustomersByIds(customerIds) {
  const axiosPriv = useAxiosPrivate();
  const queries = useQueries({
    queries: customerIds.map((id) => ({
      queryKey: ["customers", id],
      queryFn: () => customersApi.getCustomerById(axiosPriv, id),
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const data = isLoading ? null : queries.map((query) => query.data);
  return { data, isLoading };
}

export function useCustomerStatuses(options = {}) {
  const axiosPriv = useAxiosPrivate();

  const queryOpts = queryOptions({
    ...options,
    queryKey: ["customerStatuses"],
    queryFn: () => {
      return customersApi.getStatuses(axiosPriv);
    },
  });

  return useQuery({
    ...queryOpts,
  });
}

export function useCompaniesShort(options = {}) {
  const axiosPriv = useAxiosPrivate();
  return useQuery({
    ...options,
    queryKey: ["companiesShort"],
    queryFn: () => {
      return customersApi.getCompaniesShort(axiosPriv);
    },
  });
}

export function useAddCustomerPersonMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data) => {
      return customersApi.addCustomerPerson(axiosPriv, data);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useAddCompanyMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data) => {
      return customersApi.addCompany(axiosPriv, data);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useConnectPersonToCompanyMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data) => {
      return customersApi.connectPersonToCompany(axiosPriv, data);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [s] }); // TODO: more efficient invalidation for only affected customer
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useEditCustomerPersonMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  // const { onServerError } = useFormServerSideErrorsHandler()
  const { error: toastErr } = useToasts();

  return useMutation({
    ...options,
    mutationFn: (data) => {
      return customersApi.updateCustomer(axiosPriv, data.customerId, data);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
}

export function useEditCustomerCompanyMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { error: toastErr } = useToasts();

  return useMutation({
    ...options,
    mutationFn: (data) => {
      return customersApi.updateCustomerCompany(
        axiosPriv,
        data.customerId,
        data,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      toastErr(error.message);
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
}

export function useIdentificationDocuments(personId, options = {}) {
  const axiosPriv = useAxiosPrivate();

  return useQuery({
    ...options,
    queryKey: ["person", personId, "identificationDocuments"],
    queryFn: async () => {
      const response = await customersApi.getIdentificationDocuments(
        axiosPriv,
        personId,
      );
      return z.array(customersApiSchema.identificationDocument).parse(response);
    },
    enabled: !!personId && options.enabled !== false,
  });
}

export function useCreateIdentificationDocument(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({ personId, data }) => {
      return await customersApi.createIdentificationDocument(
        axiosPriv,
        personId,
        data,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["person", variables.personId, "identificationDocuments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["person", variables.personId],
      });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["agreements"] });
      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateIdentificationDocument(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({ personId, documentId, data }) => {
      return await customersApi.updateIdentificationDocument(
        axiosPriv,
        personId,
        documentId,
        data,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["person", variables.personId, "identificationDocuments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["person", variables.personId],
      });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["agreements"] });
      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useDeleteIdentificationDocument(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({ personId, documentId }) => {
      return await customersApi.deleteIdentificationDocument(
        axiosPriv,
        personId,
        documentId,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["person", variables.personId, "identificationDocuments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["person", variables.personId],
      });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["agreements"] });
      options.onSuccess?.(data, variables, context);
    },
  });
}

export function getCustomerDisplayName(customer) {
  if (customer.company) {
    return customer.company.name;
  }
  return `${customer.person.name} ${customer.person.surname}`;
}

export function useRemovePersonReferencyMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data) => {
      return customersApi.removePersonReferency(axiosPriv, data);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}


export function useRemovePersonComapnyMutation(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (id) => {
      return customersApi.deletePersonCompany(axiosPriv, id);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function usePerson(personId, options = {}) {
  const axiosPriv = useAxiosPrivate();

  const queryOpts = queryOptions({
    ...options,
    queryKey: ["person", personId],
    queryFn: async () => {
      const response = await customersApi.getPerson(axiosPriv, personId);
      return customersApiSchema.person.parse(response);
    },
  });

  return useQuery({
    ...queryOpts,
  });
}

const PutUpdatePersonSchema = z.object({
  person_name: z.string().optional(),
  person_name2: z.string().optional().nullable(),
  person_surname: z.string().optional(),
  person_pesel: z.string().optional().nullable(),
  person_nip: z.string().optional().nullable(),
  person_name_of_father: z.string().optional().nullable(),
  person_name_of_mother: z.string().optional().nullable(),
  person_financial_status: z.string().optional().nullable(),
  person_marital_status: z.string().optional().nullable(),
  person_maiden_surname: z.string().optional().nullable(),
});

export function useUpdatePerson(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { onServerError } = useFormServerSideErrorsHandler()
  return useMutation({
    ...options,
    mutationFn: async (data) => {
      return await customersApi.updatePerson(axiosPriv, data.id, data.body);
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: ["person", variables.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["customers", "byPersonId", variables.id],
      });
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["agreements"] });
      options.onSuccess?.(data, variables, context);
    },
    onError: onServerError
  });
}

export function usePhonesForPersonId(
  personId,
  params = new URLSearchParams(),
  options = {},
) {
  if (!personId) {
    return Promise.resolve({});
  }

  const axiosPriv = useAxiosPrivate();

  return useQuery({
    ...options,
    queryKey: ["phones", personId],
    queryFn: () => {
      return customersApi.getPhonesForPerson(axiosPriv, personId, params);
    },
  });
}

export function useAdditionalPhonesForPersonId(
  personId,
  params = new URLSearchParams(),
  options = {},
) {
  params.set("persphon_primary", "false");
  return usePhonesForPersonId(personId, params, options);
}

export function useEmailsForPersonId(
  personId,
  params = new URLSearchParams(),
  options = {},
) {
  if (!personId) {
    return Promise.resolve({});
  }

  const axiosPriv = useAxiosPrivate();

  return useQuery({
    ...options,
    queryKey: ["emails", personId],
    queryFn: () => {
      return customersApi.getMailsForPerson(axiosPriv, personId, params);
    },
  });
}

export function useAdditionalEmailsForPersonId(
  personId,
  params = new URLSearchParams(),
  options = {},
) {
  params.set("persmail_primary", "false");
  return useEmailsForPersonId(personId, params, options);
}

export function useUpdateAdditialPhonesForPersonId(personId, options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { onServerError } = useFormServerSideErrorsHandler();
  return useMutation({
    ...options,
    mutationFn: (phones) => {
      return customersApi.updateAdditionalPhonesForPerson(
        axiosPriv,
        personId,
        phones,
      );
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["phones", personId] });
      options.onSuccess?.(data, variables, context);
    },
    onError: onServerError,
  });
}

export function useUpdateAdditialEmailsForPersonId(personId, options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { onServerError } = useFormServerSideErrorsHandler();
  return useMutation({
    ...options,
    mutationFn: (emails) => {
      return customersApi.updateAdditionalMailsForPerson(
        axiosPriv,
        personId,
        emails,
      );
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["emails", personId] });
      options.onSuccess?.(data, variables, context);
    },
    onError: onServerError,
  });
}

export function usePhonesForCompanyId(
  companyId,
  params = new URLSearchParams(),
  options = {},
) {
  if (!companyId) {
    return Promise.resolve({});
  }

  const axiosPriv = useAxiosPrivate();

  return useQuery({
    ...options,
    queryKey: ["phones", companyId],
    queryFn: () => {
      return customersApi.getPhonesForCompany(axiosPriv, companyId, params);
    },
  });
}

export function useAdditionalPhonesForCompanyId(
  personId,
  params = new URLSearchParams(),
  options = {},
) {
  params.set("comphon_primary", "false");
  return usePhonesForCompanyId(personId, params, options);
}

export function useEmailsForCompanyId(
  companyId,
  params = new URLSearchParams(),
  options = {},
) {
  if (!companyId) {
    return Promise.resolve({});
  }

  const axiosPriv = useAxiosPrivate();

  return useQuery({
    ...options,
    queryKey: ["emails", companyId],
    queryFn: () => {
      return customersApi.getMailsForCompany(axiosPriv, companyId, params);
    },
  });
}

export function useAdditionalEmailsForCompanyId(
  comanyId,
  params = new URLSearchParams(),
  options = {},
) {
  params.set("commail_primary", "false");
  return useEmailsForCompanyId(comanyId, params, options);
}

export function useUpdateAdditialPhonesForCompanyId(companyId, options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { onServerError } = useFormServerSideErrorsHandler();
  return useMutation({
    ...options,
    mutationFn: (phones) => {
      return customersApi.updateAdditionalPhonesForCompany(
        axiosPriv,
        companyId,
        phones,
      );
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["phones", companyId] });
      options.onSuccess?.(data, variables, context);
    },
    onError: onServerError,
  });
}

export function useUpdateAdditialEmailsForCompanyId(companyId, options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { onServerError } = useFormServerSideErrorsHandler();
  return useMutation({
    ...options,
    mutationFn: (emails) => {
      return customersApi.updateAdditionalMailsForCompany(
        axiosPriv,
        companyId,
        emails,
      );
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["emails", companyId] });
      options.onSuccess?.(data, variables, context);
    },
    onError: onServerError,
  });
}

export function useAnonymizeCustomer(options = {}) {
  const axiosPriv = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { onServerError } = useFormServerSideErrorsHandler();
  return useMutation({
    ...options,
    mutationFn: (requestData) => {
      return customersApi.anonymizeCustomer(
        axiosPriv,
        requestData,
      );
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["clauses"] });
      options.onSuccess?.(data, variables, context);
    },
    onError: onServerError,
  });
}

export const customersApi = {
  getReasons: async (axios) => {
    const response = await axios.get(apiUrls.customers.reasons);
    return customersApiSchema.getReasonsResonse.parse(response.data);
  },

  getCustomers: (axios, params) => {
    return axios
      .get(apiUrls.customers.all, {
        params,
      })
      .then((response) => response.data);
  },

  getCustomersShortData: (axios, params) => {
    return axios
      .get(apiUrls.customers.customershortData, {
        params,
      })
      .then((response) => response.data);
  },

  getCustomersApprovedClauses: (axios, params) => {
    return axios
      .get(apiUrls.customers.customerApprovedClauses, {
        params,
      })
      .then((response) => response.data);
  },

  getCustomersSymfoniaCode: (axios, params) => {
    return axios
      .get(apiUrls.customers.customerSymfoniaCode, {
        params,
      })
      .then((response) => response.data);
  },

  async updateCustomerSymfoniaCode(axios, customerId, data) {
    const response = await axios.patch(
      apiUrls.customers.customerSymfoniaCodeUpdate(customerId),
      {
        customer_symfonia_code: data.customer_symfonia_code,
      },
    );
    return response.data;
  },

  getCustomersFullData: (axios, params) => {
    return axios
      .get(apiUrls.customers.customersFullData, {
        params,
      })
      .then((response) => response.data);
  },

  getCustomerById: (axios, customerId) => {
    return axios
      .get(apiUrls.customers.customer(customerId))
      .then((response) => response.data);
  },

  updateCustomer: async (axios, customerId, data) => {
    const response = await axios.put(
      apiUrls.customers.persons.edit(customerId),
      {
        note: data.note,
        person: {
          email: data.email
            ? {
                email_address: data.email,
              }
            : null,
          phone: data.phone
            ? {
                phone_number: data.phone,
                phone_note: data.phone_note,
              }
            : null,
          // clause_decisions: data.clause_decisions,
          active: data.active,
          person_name: data.person_name,
          person_name2: data.person_name2,
          person_surname: data.person_surname,
          person_pesel: data.person_pesel,
          person_idcard: data.person_idcard,
          person_passport: data.person_passport,
        },
        reason: data.reason,
        address_living: data.address_living,
        address_correspondence: data.address_correspondence,
        related_persons: data.related_persons,
        jdg_nip: data.jdg_nip,
        jdg_regon: data.jdg_regon,
        jdg_company_name: data.jdg_company_name,
      },
    );
    return response.data;
  },

  async updateCustomerCompany(axios, customerId, data) {
    const response = await axios.put(
      apiUrls.customers.companies.edit(customerId),
      {
        note: data.note,
        company: {
          email: data.email
            ? {
                email_address: data.email,
              }
            : null,
          phone: data.phone
            ? {
                phone_number: data.phone,
                phone_note: data.phone_note,
              }
            : null,
          name: data.name,
          name_short: data.name_short,
          nip: data.nip,
          regon: data.regon,
          legal: data.legal,
          krs: data.krs,
          court: data.court,
          signature: data.signature,
          capital: data.capital,
          representation: data.representation,
          tax: data.tax,
          company_citizenship: data.company_citizenship,
        },
        reason: null,
        company_address:
          data.city ||
          data.street ||
          data.building ||
          data.flat_nr ||
          data.zip_code
            ? {
                city: data.city || "",
                zip_code: data.zip_code || "",
                street: data.street || "",
                building: data.building || "",
                flat_nr: data.flat_nr || "",
              }
            : null,
        mailing_address: data.mailing_address,
        related_persons: data.related_persons,
      },
    );
    return response.data;
  },

  async getStatuses(axios) {
    return axios
      .get(apiUrls.customers.statuses)
      .then((response) =>
        statusApiSchema.getStatusesResponse.parse(response.data),
      );
  },

  getCompaniesShort(axios) {
    return axios
      .get(apiUrls.customers.companies.shortAll)
      .then((response) => response.data);
  },

  async addCustomerPerson(axios, data) {
    const response = await axios.post(apiUrls.customers.person, data);
    return response.data;
  },

  async addCompany(axios, data) {
    const response = await axios.post(apiUrls.customers.company, {
      note: data.note,
      company: {
        email: {
          email_address: data.email,
        },
        phone:
          data.phone === ""
            ? null
            : {
                phone_number: data.phone,
                phone_note: data.phone_note || "",
              },
        name: data.name,
        name_short: data.name_short,
        nip: data.nip,
        regon: data.regon,
        legal: data.legal,
        krs: data.krs,
        court: data.court,
        signature: data.signature,
        capital: data.capital,
        representation: data.representation,
        tax: data.tax,
        additional_emails: data.additional_emails,
        additional_phones: data.additional_phones,
      },
      reason: null,
      company_address:
        data.city ||
        data.street ||
        data.building ||
        data.flat_nr ||
        data.zip_code
          ? {
              city: data.city,
              zip_code: data.zip_code,
              street: data.street,
              building: data.building,
              flat_nr: data.flat_nr,
            }
          : null,
      mailing_address: data.mailing_address,
      related_persons: data.related_persons,
    });
    return response.data;
  },

  async connectPersonToCompany(axios, data) {
    return axios
      .post(apiUrls.customers.personCompany, {
        person_id: data.personId,
        company_id: data.companyId,
        role: data.role,
      })
      .then((response) => response.data);
  },

  async deletePersonCompany(axios, id) {
    return await axios.delete(apiUrls.customers.personCompanyDelete(id))
  },

  async removePersonReferency(axios, data) {
    const response = await axios.delete(apiUrls.sales.persons.removeReferency, {
      data: {
        primary_person_id: data.primary_person_id,
        secondary_person_id: data.secondary_person_id,
      },
    });
    return response.data;
  },

  async getPerson(axios, personId) {
    const response = await axios.get(
      apiUrls.customers.persons.detail(personId),
    );
    return customersApiSchema.person.parse(response.data);
  },

  async updatePerson(axios, personId, data) {
    const response = await axios.patch(
      apiUrls.customers.persons.update(personId),
      data,
    );
    return response.data;
  },

  async getIdentificationDocuments(axios, personId) {
    const response = await axios.get(
      apiUrls.customers.persons.identificationDocuments.list(personId),
    );
    return response.data;
  },

  async createIdentificationDocument(axios, personId, data) {
    const response = await axios.post(
      apiUrls.customers.persons.identificationDocuments.list(personId),
      data,
    );
    return customersApiSchema.identificationDocument.parse(response.data);
  },

  async updateIdentificationDocument(axios, personId, documentId, data) {
    const response = await axios.patch(
      apiUrls.customers.persons.identificationDocuments.detail(
        personId,
        documentId,
      ),
      data,
    );
    return customersApiSchema.identificationDocument.parse(response.data);
  },

  async deleteIdentificationDocument(axios, personId, documentId) {
    await axios.delete(
      apiUrls.customers.persons.identificationDocuments.detail(
        personId,
        documentId,
      ),
    );
  },

  getPhonesForPerson(axios, personId, params = new URLSearchParams()) {
    return axios
      .get(apiUrls.customers.persons.phones(personId), { params })
      .then((response) => response.data);
  },

  updateAdditionalPhonesForPerson(axios, personId, phones) {
    return axios.post(
      apiUrls.customers.persons.updateAdditionalPhones(personId),
      { phones },
    );
  },

  getMailsForPerson(axios, personId, params = new URLSearchParams()) {
    return axios
      .get(apiUrls.customers.persons.mails(personId), { params })
      .then((response) => response.data);
  },

  updateAdditionalMailsForPerson(axios, personId, emails) {
    return axios.post(
      apiUrls.customers.persons.updateAdditionalMails(personId),
      { emails },
    );
  },

    getPhonesForCompany(axios, companyId, params = new URLSearchParams()) {
    return axios
      .get(apiUrls.customers.companies.phones(companyId), { params })
      .then((response) => response.data);
  },

  updateAdditionalPhonesForCompany(axios, companyId, phones) {
    return axios.post(
      apiUrls.customers.companies.updateAdditionalPhones(companyId),
      { phones },
    );
  },

  getMailsForCompany(axios, companyId, params = new URLSearchParams()) {
    return axios
      .get(apiUrls.customers.companies.mails(companyId), { params })
      .then((response) => response.data);
  },

  updateAdditionalMailsForCompany(axios, companyId, emails) {
    return axios.post(
      apiUrls.customers.companies.updateAdditionalMails(companyId),
      { emails },
    );
  },

  anonymizeCustomer(axios, requestData) {
    return axios.post(
      apiUrls.customers.customerAnonymization,
      requestData,
    );
  },

  getPersonSelectList(axios, params = new URLSearchParams()) {
    return axios
      .get(apiUrls.customers.personSelectList, { params })
      .then((response) => response.data);
  },
};

export function getPersonFullName(person) {
  if (!person) {
    return "";
  }
  return [person.name, person.name2, person.surname].filter(Boolean).join(" ");
}

export function joinCustomerNames(customers = []) {
  return customers
    .map((c) => {
      const p = c?.person;
      if (p) {
        const full = getPersonFullName(p);
        if (full) {
          return full;
        }
      }
      const comp = c?.company;
      if (comp) {
        // prefer full company name if present, fall back to name
        return (
          comp.company_full_name ||
          comp.name ||
          comp.company_name ||
          c?.id ||
          "-"
        );
      }
      return c?.id || "-";
    })
    .filter(Boolean)
    .join(", ");
}
